import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/* Google Analytics 4, wired for this specific site.

   Two things a copy-pasted gtag snippet gets wrong here:

   1. This is a single-page app. React Router changes pages without a
      reload, so GA would only ever record the first page a visitor lands
      on. We disable gtag's own page_view and send one per route change.

   2. The prerender build (scripts/prerender.mjs) drives a headless browser
      over all 18 routes on localhost. Left unguarded that fires 18 phantom
      pageviews into the reports on every deploy. Non-production hosts are
      blocked below — which also keeps `npm run dev` and Vercel previews out
      of the data.

   The measurement ID is public (it ships in the page source), so it has a
   default and needs no Vercel config to work. Set VITE_GA_ID to a different
   ID to point elsewhere, or to an empty string to switch analytics off —
   ?? not || so that an intentional empty string wins over the default. */
const GA_ID = import.meta.env.VITE_GA_ID ?? 'G-ELMVCREK5E';

/* Analytics covers the public marketing surface only. Product URLs can carry
   experiment share tokens and project identifiers — none of that should reach
   Google, and the privacy policy commits to not recording it. */
const MARKETING_PATHS = new Set([
  '/', '/notebook', '/cinematic', '/machining', '/start', '/pricing',
  '/security', '/faq', '/resources', '/contact', '/privacy', '/terms',
  '/accessibility',
]);
const MARKETING_PREFIXES = ['/for/', '/resources/'];
const isMarketingPath = (path) =>
  MARKETING_PATHS.has(path) || MARKETING_PREFIXES.some((prefix) => path.startsWith(prefix));

const isTrackableHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.local')) return false;
  if (host.endsWith('.vercel.app')) return false; // preview deployments
  return true;
};

let scriptRequested = false;

/* Loads gtag once, on the first tracked navigation. Returns false when
   analytics is disabled or the host isn't production. */
function ensureGtag() {
  if (!GA_ID || !isTrackableHost()) return false;
  if (scriptRequested) return true;
  scriptRequested = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
  return true;
}

/* Sends a page_view per client-side navigation. Mounted inside <Router> but
   above the routes, so each page's useDocumentMeta effect (a child) has
   already set document.title before this runs; the extra frame covers
   pages that set their title asynchronously. */
export default function useAnalytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const marketing = isMarketingPath(pathname);
    /* GA's official kill switch. Set before the script loads, and flipped on
       every navigation, so entering the product also stops enhanced-measurement
       events (scroll, outbound clicks) that would otherwise report app URLs. */
    if (GA_ID && typeof window !== 'undefined') window[`ga-disable-${GA_ID}`] = !marketing;
    if (!marketing) return undefined;
    if (!ensureGtag()) return undefined;
    const frame = requestAnimationFrame(() => {
      window.gtag('event', 'page_view', {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, search]);
}
